import { useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import DashboardWidgetCard from '../../Dashboard/DashboardWidgetCard';
import { BRAND_SECONDARY_MAIN } from '../../../theme/managementTheme';
import {
  getCartesianGridStroke,
  getRechartsAxisStroke,
  getRechartsTooltipProps,
} from '../../../util/chartTheme';
import { formatNumber } from './dashboardFormat';

// Fills every day of the from–to range so days without new contacts render as 0.
const buildSeries = (newContacts, fallbackFrom, fallbackTo) => {
  const from = dayjs(newContacts?.from || fallbackFrom).startOf('day');
  const to = dayjs(newContacts?.to || fallbackTo).startOf('day');
  if (!from.isValid() || !to.isValid() || from.isAfter(to)) return [];
  const counts = new Map(
    (Array.isArray(newContacts?.timeline) ? newContacts.timeline : []).map(entry => [
      entry.date,
      Number(entry.count) || 0,
    ])
  );
  const series = [];
  let cursor = from;
  // Hard cap guards against a malformed range producing an endless loop.
  let guard = 0;
  while (!cursor.isAfter(to) && guard < 1000) {
    const key = cursor.format('YYYY-MM-DD');
    series.push({ date: key, count: counts.get(key) || 0 });
    cursor = cursor.add(1, 'day');
    guard += 1;
  }
  return series;
};

// Widget 6 — new contacts over time (area chart) with a selectable date range.
export default function NewContactsWidget({
  newContacts,
  range,
  onRangeChange,
  loading,
  error,
  onRetry,
}) {
  const theme = useTheme();
  const [dialogOpen, setDialogOpen] = useState(false);
  const fromRef = useRef(null);
  const toRef = useRef(null);

  const series = useMemo(
    () => buildSeries(newContacts, range.from, range.to),
    [newContacts, range.from, range.to]
  );

  // Deep brand blue works on light surfaces; on dark surfaces the theme's
  // lighter primary blue keeps the required contrast against the background.
  const stroke = theme.palette.mode === 'light' ? BRAND_SECONDARY_MAIN : theme.palette.primary.main;
  const tooltipProps = getRechartsTooltipProps(theme);

  const handleApplyRange = () => {
    const from = fromRef.current?.value;
    const to = toRef.current?.value;
    if (!from || !to || dayjs(from).isAfter(dayjs(to))) {
      toast.error('Bitte einen gültigen Zeitraum wählen („von“ darf nicht nach „bis“ liegen).');
      return;
    }
    setDialogOpen(false);
    onRangeChange({ from, to });
  };

  return (
    <>
      <DashboardWidgetCard
        title='Neue Kontakte'
        icon='PersonAdd'
        accent='success'
        loading={loading}
        error={error}
        onRetry={onRetry}
        headerAction={
          <Tooltip title='Zeitraum wählen'>
            <IconButton
              size='small'
              onClick={() => setDialogOpen(true)}
              aria-label='Zeitraum wählen'
            >
              <MoreVertIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        }
        footer={
          <Typography variant='caption' color='text.secondary'>
            Zeitraum: {dayjs(range.from).format('DD.MM.YYYY')} –{' '}
            {dayjs(range.to).format('DD.MM.YYYY')}
          </Typography>
        }
      >
        <Stack spacing={1} sx={{ flexGrow: 1 }}>
          <Stack direction='row' alignItems='baseline' spacing={1}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', lineHeight: 1, color: stroke }}>
              {formatNumber(newContacts?.total)}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Kontakte hinzugefügt
            </Typography>
          </Stack>

          <Box sx={{ width: '100%', height: 180 }}>
            <ResponsiveContainer width='100%' height='100%'>
              <AreaChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id='emailMarketingNewContactsFill' x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='0%' stopColor={stroke} stopOpacity={0.35} />
                    <stop offset='100%' stopColor={stroke} stopOpacity={0.03} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  stroke={getCartesianGridStroke(theme)}
                  strokeDasharray='3 3'
                  vertical={false}
                />
                <XAxis
                  dataKey='date'
                  tickFormatter={value => dayjs(value).format('DD.MM.')}
                  stroke={getRechartsAxisStroke(theme)}
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  minTickGap={24}
                />
                <YAxis
                  allowDecimals={false}
                  width={32}
                  stroke={getRechartsAxisStroke(theme)}
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                />
                <RechartsTooltip
                  {...tooltipProps}
                  labelFormatter={value => dayjs(value).format('DD.MM.YYYY')}
                  formatter={value => [formatNumber(value), 'Neue Kontakte']}
                />
                <Area
                  type='monotone'
                  dataKey='count'
                  name='Neue Kontakte'
                  stroke={stroke}
                  strokeWidth={2}
                  fill='url(#emailMarketingNewContactsFill)'
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        </Stack>
      </DashboardWidgetCard>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth='xs'>
        <DialogTitle>Zeitraum wählen</DialogTitle>
        <DialogContent>
          <Stack direction='row' spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              size='small'
              type='date'
              label='Von'
              inputRef={fromRef}
              defaultValue={range.from}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              size='small'
              type='date'
              label='Bis'
              inputRef={toRef}
              defaultValue={range.to}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Abbrechen</Button>
          <Button variant='contained' onClick={handleApplyRange}>
            Übernehmen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
